import { isNil } from 'lodash';
import { EngineColorValue } from './recipe-types';

// engine color recipe
export class EngineColorRecipeModel {

  tintColor?: EngineColorValue[] | false;
  saturation?: EngineColorValue | false;
  contrast?: EngineColorValue | false;
  brightness?: EngineColorValue | false;
  opacity?: EngineColorValue | false;

  static isFresnel (value: any): boolean {
    return Array.isArray(value) && value.length === 5;
  }

  static isColor (value: any): boolean {
    return Array.isArray(value) && value.length === 3;
  }

  constructor(value: EngineColorRecipeModel = {}) {
    const { tintColor, saturation, contrast, brightness, opacity } = value;
    this.tintColor = tintColor;
    this.saturation = saturation;
    this.contrast = contrast;
    this.brightness = brightness;
    this.opacity = opacity;
  }

  // these objects are not comparing properly using isEqual
  // here we are sorting the keys and comparing them by string value
  static isEqual(color: EngineColorRecipeModel, toColor: EngineColorRecipeModel): boolean {
    if (color && toColor) {
      return JSON.stringify(color, Object.keys(color).sort()) === JSON.stringify(toColor, Object.keys(toColor).sort());
    }
    return false;
  }

  // If all values are false, the recipe isn't a real recipe. This filters out a lot of bad data on 
  // empty objects. 
  static isRealRecipe(recipe: EngineColorRecipeModel): boolean {
    return (!isNil(recipe.brightness) && recipe.brightness !== false) ||
      (!isNil(recipe.contrast) && recipe.contrast !== false) ||
      (!isNil(recipe.opacity) && recipe.opacity !== false) ||
      (!isNil(recipe.saturation) && recipe.saturation !== false) ||
      (!isNil(recipe.tintColor) && recipe.tintColor !== false);
  }

  static compare(color: EngineColorRecipeModel, toColor: EngineColorRecipeModel): number {
    try {
      const colorHue = EngineColorRecipeModel.hue(color);
      const toColorHue = EngineColorRecipeModel.hue(toColor);
      const opacity = color.opacity === false ? 1.0 : color.opacity;
      const toOpacity = toColor.opacity === false ? 1.0 : toColor.opacity;
      if (colorHue === toColorHue) {
        if (opacity === toOpacity) {
          if (color.saturation === toColor.saturation) {
            if (color.brightness === toColor.brightness) {
              return color.contrast > toColor.contrast ? colorHue + 4 : colorHue - 4;
            }
            return color.brightness > toColor.brightness ? colorHue + 3 : colorHue - 3;
          }
          return color.saturation > toColor.saturation ? colorHue + 2 : colorHue - 2;
        }
        return opacity > toOpacity ? colorHue + 1 : colorHue - 1;
      }
      return colorHue - toColorHue;
    } catch (error) {
      return 0;
    }
  }

  static hue(color: EngineColorRecipeModel): number {
    if (!color.tintColor) {
      return -10;
    }

    const red = color.tintColor[0] as number;
    const green = color.tintColor[1] as number;
    const blue = color.tintColor[2] as number;

    const colorMax = Math.max(red, green, blue);
    const colorMin = Math.min(red, green, blue);
    const delta = colorMax - colorMin;

    
    let hue = 0;
    if (colorMax === red) {
      hue = (green - blue) / delta;
    } else if (colorMax === green) {
      hue = 2 + ((blue - red) / delta);
    } else {
      hue = 4 + ((red - green) / delta);
    }
    hue = hue * 60;
    if (hue < 0) {
      hue = hue + 360;
    }
    return hue;
  }

  static generateUniqueRecipes(recipes: EngineColorRecipeModel[]) {
    const uniqueRecipes = [];
    recipes.forEach(recipe  => {
      const index = EngineColorRecipeModel.recipeIndexOf(uniqueRecipes, recipe);
      if (index === -1) {
        uniqueRecipes.push(recipe);
      }
    });
    
    return uniqueRecipes;
  }

  static recipeIndexOf (array: EngineColorRecipeModel[], recipe: EngineColorRecipeModel): number {
    return array.findIndex(arrayRecipe => EngineColorRecipeModel.isEqual(arrayRecipe, recipe));
  }
}
